import { colors, date, intl } from '@beelday/common'
import styled from '@emotion/styled'
import { useSelector } from 'app-redux'
import { redux as scriptRedux } from 'features/script'
import useScript from 'features/script/use-script'
import { uniq } from 'lodash'
import { FC, useMemo } from 'react'
import { roomEndUsers } from 'room/common/redux'
import { useStageSurface } from 'room/common/stage-surface'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { chunkHours, LobbyRole } from 'room/lobby/lobby-view'
import { LobbyAction } from 'room/lobby/presentation/lobby-action'
import { FactItem, LobbyFacts } from 'room/lobby/presentation/lobby-facts'
import { LobbyHero } from 'room/lobby/presentation/lobby-hero'
import { LobbySize, SCALE } from 'room/lobby/presentation/lobby-size'
import { LobbyStatus } from 'room/lobby/presentation/lobby-status'
import { useLobbyRole } from 'room/lobby/use-lobby-view'
import welcomePicture from './images/in-person-welcome.png'

const Note = styled.p<{ $size: LobbySize }>`
	margin: 0;
	color: ${colors.lightGray};
	font-size: ${props => SCALE[props.$size].small}px;
	line-height: 1.4;
`

type Props = {
	size: Exclude<LobbySize, 'phone'>
	/** The trainer in their own window; everyone else is the room. */
	role: LobbyRole
	subject?: string
	/** The day's planned hours, already formatted. */
	hours?: string
	venue?: string
	attendance: { present: number; expected: number }
	/** The agenda has items, so there is something to start. */
	canStart: boolean
	onStart: () => void
}

/**
 * The in-person stage before its agenda starts - the trainer's room and the
 * projector, which would otherwise be blank: the lobby's card, welcoming. The
 * trainer starts the agenda from it (the room header's Start script bar
 * makes way for it); the room is told the screen changes by itself.
 */
export const InPersonWelcomeCard: FC<Props> = ({
	size,
	role,
	subject,
	hours,
	venue,
	attendance,
	canStart,
	onStart,
}) => {
	const trainer = role === 'trainer'
	const facts: FactItem[] = [
		...(venue ? [{ id: 'venue', text: venue }] : []),
		{
			id: 'attendance',
			text: (
				<intl.Translate
					present={attendance.present}
					expected={attendance.expected}
				>
					in_person.lobby.attendance
				</intl.Translate>
			),
		},
	]

	return (
		<LobbyHero
			size={size}
			illustration={welcomePicture}
			eyebrow={<intl.Translate>in_person.welcome.eyebrow</intl.Translate>}
			heading={
				<intl.Translate>
					{trainer
						? 'in_person.welcome.heading.trainer'
						: 'in_person.welcome.heading.room'}
				</intl.Translate>
			}
			subject={subject}
			hours={hours}
			facts={<LobbyFacts size={size} facts={facts} />}
			testId="in_person.welcome"
		>
			{trainer && canStart ? (
				<>
					<LobbyAction onClick={onStart} testId="in_person.welcome.start">
						<intl.Translate>in_person.welcome.start</intl.Translate>
					</LobbyAction>
					<Note $size={size}>
						<intl.Translate>in_person.welcome.note</intl.Translate>
					</Note>
				</>
			) : (
				<LobbyStatus size={size}>
					<intl.Translate>
						{trainer
							? 'in_person.welcome.no_agenda'
							: 'in_person.welcome.status'}
					</intl.Translate>
				</LobbyStatus>
			)}
		</LobbyHero>
	)
}

/** `InPersonWelcomeCard` for this session, sized as the lobby is. */
export const InPersonWelcome: FC = () => {
	const role = useLobbyRole()
	const onProjector = useStageSurface() === 'projector'
	const training = useSelector(state => state.sessionInfo.training)
	const chunk = useSelector(
		state => state.script.chunks[state.script.currentSessionChunkIndex]
	)
	const canStart = useSelector(scriptRedux.selectScriptHasItems)
	const endUsers = useSelector(roomEndUsers)
	const { formatTime } = date.useDateFormat()
	const script = useScript()
	const address = useAssertedJoinedRoomAddress()
	const present = useMemo(
		() => uniq(endUsers.map(user => user.id)).length,
		[endUsers]
	)

	return (
		<InPersonWelcomeCard
			size={onProjector ? 'room' : 'desktop'}
			role={role}
			subject={training?.subject}
			hours={chunkHours(chunk, formatTime)}
			venue={training?.venue}
			attendance={{ present, expected: training?.participants.length ?? 0 }}
			canStart={canStart}
			onStart={() => script.start(address)}
		/>
	)
}
